import type { QueryClient } from '@tanstack/react-query';

import { invalidateCanvasHistoryQueriesForAsset } from '@/hooks/api/useCanvasSnapshot';
import type { ProjectElementTagResponse } from '@/types/project-element-tag';
import type { StoryboardItem } from '@/types/storyboard';

const PROJECT_ELEMENT_TAGS_ROOT = 'project-element-tags';

export interface TagSelectedResourceChangedCacheSyncResult {
  changedStoryboardItemIds: string[];
}

export function mapElementTypeToAssetType(elementType?: string | null): string | undefined {
  const raw = elementType?.trim();
  if (!raw) return undefined;
  if (raw === '1' || raw === 'character') return 'character';
  if (raw === '2' || raw === 'location' || raw === 'scene') return 'location';
  if (raw === '3' || raw === 'prop') return 'prop';
  return undefined;
}

/**
 * B 收到标签定稿变更时：就地更新标签缓存与分镜资产关联节点的 resourceId / 封面，
 * 并失活资产画布 history（定稿角标）。封面 URL 缺省时仍依赖后续 invalidate 重拉。
 * 仅当资产关联节点的定稿 resourceId 真变了才返回其 itemId，供画布红点使用。
 */
export function applyTagSelectedResourceChangedCacheSync(
  queryClient: QueryClient,
  payload: {
    projectId?: string | null;
    tagId?: string | null;
    elementId?: string | null;
    elementType?: string | null;
    selectedResourceId?: string | null;
    tagImageUrl?: string | null;
  },
  options?: { includeSnapshot?: boolean }
): TagSelectedResourceChangedCacheSyncResult {
  const tagId = payload.tagId?.trim();
  const selectedResourceId = payload.selectedResourceId?.trim();
  if (!tagId || !selectedResourceId) {
    return { changedStoryboardItemIds: [] };
  }

  const tagImageUrl = payload.tagImageUrl?.trim() || null;
  const changedStoryboardItemIds = new Set<string>();

  queryClient.setQueriesData<ProjectElementTagResponse[]>(
    {
      predicate: (query) =>
        Array.isArray(query.queryKey) &&
        query.queryKey[0] === PROJECT_ELEMENT_TAGS_ROOT &&
        Array.isArray(query.state.data),
    },
    (current) =>
      current?.map((tag) => {
        if (tag.tagId !== tagId) return tag;
        return {
          ...tag,
          selectedResourceId,
          ...(tagImageUrl
            ? {
                tagImageUrl,
                resourceUrl: tagImageUrl,
              }
            : {}),
        };
      }) ?? current
  );

  queryClient.setQueriesData<StoryboardItem[]>(
    {
      predicate: (query) =>
        Array.isArray(query.queryKey) &&
        query.queryKey[0] === 'storyboard-items' &&
        Array.isArray(query.state.data),
    },
    (current) =>
      current?.map((item) => {
        if (item.itemType !== 1 || item.elementTagId !== tagId) return item;
        if ((item.resourceId ?? '').trim() !== selectedResourceId) {
          changedStoryboardItemIds.add(item.itemId);
        }
        return {
          ...item,
          resourceId: selectedResourceId,
          ...(tagImageUrl ? { coverUrl: tagImageUrl } : {}),
        };
      }) ?? current
  );

  const projectId = payload.projectId?.trim();
  const elementId = payload.elementId?.trim();
  if (projectId && elementId) {
    invalidateCanvasHistoryQueriesForAsset(queryClient, {
      projectId,
      assetId: elementId,
      assetType: mapElementTypeToAssetType(payload.elementType),
      includeSnapshot: options?.includeSnapshot,
    });
  }

  return { changedStoryboardItemIds: [...changedStoryboardItemIds] };
}
